import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { CheckCircle, AlertCircle, Loader2, ShieldCheck, Clock, FileText, Mail, ExternalLink, Calendar, Lock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const COURSE_OPTIONS = [
  { value: '', label: 'Select a course' },
  { value: 'bsc', label: 'BSc Nautical Science' },
  { value: 'btech', label: 'B.Tech Marine Engineering' },
  { value: 'dns', label: 'DNS (Diploma in Nautical Science)' }
];

function PublicRegistration() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [regStatus, setRegStatus] = useState('checking'); // 'checking' | 'open' | 'closed'
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    course_choice_1: '',
    course_choice_2: '',
    course_choice_3: '',
    has_applied: null, // null = not selected, true = yes, false = no
    application_number: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [acceptMarketing, setAcceptMarketing] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);

  useEffect(() => {
    api.get('/public/registration-status')
      .then((res) => setRegStatus(res.data.enabled ? 'open' : 'closed'))
      .catch(() => setRegStatus('closed'));
  }, []);

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name || formData.name.length < 3) {
      newErrors.name = 'Name must be at least 3 characters';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone validation
    const phoneRegex = /^\d{10}$/;
    if (!formData.phone || !phoneRegex.test(formData.phone)) {
      newErrors.phone = 'Phone number must be exactly 10 digits';
    }

    // Course choices validation - at least 1st choice is required
    if (!formData.course_choice_1) {
      newErrors.course_choice_1 = 'Please select your 1st choice course';
    }

    // HIMT Application validation
    if (formData.has_applied === null) {
      newErrors.has_applied = 'Please indicate if you have applied to HIMT College';
    }
    if (formData.has_applied === true && !formData.application_number) {
      newErrors.application_number = 'Please enter your application number';
    }

    // Password validation
    if (!formData.password || formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else {
      if (!/\d/.test(formData.password)) {
        newErrors.password = 'Password must contain at least one number';
      } else if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(formData.password)) {
        newErrors.password = 'Password must contain at least one special character';
      }
    }

    // Confirm password
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Terms and conditions validation
    if (!acceptTerms) {
      newErrors.terms = 'You must accept the Terms & Conditions';
    }
    if (!acceptMarketing) {
      newErrors.marketing = 'You must accept to receive updates from HIMT College';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/public/register', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        course_choice_1: formData.course_choice_1,
        course_choice_2: formData.course_choice_2 || null,
        course_choice_3: formData.course_choice_3 || null,
        has_applied: formData.has_applied,
        application_number: formData.has_applied ? formData.application_number : null,
        password: formData.password,
        accept_marketing: acceptMarketing
      });

      setSuccess(true);
      toast.success('Registration successful! Please check your email to verify your account.');
    } catch (error) {
      console.error('Registration error:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (regStatus === 'checking') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4" data-testid="registration-loading">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (regStatus === 'closed') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center" data-testid="registration-closed">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-10 h-10 text-gray-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Registration Closed</h2>
          <p className="text-gray-600 mb-6">
            Mock IMU-CET registration is currently closed. Please check back later or contact HIMT for more information.
          </p>
          <Button onClick={() => navigate('/login')} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="closed-go-login">
            Go to Login
          </Button>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Registration Successful!</h2>
          <p className="text-gray-600 mb-4">
            Please verify your mobile number to activate your account.
          </p>
          
          {/* WhatsApp OTP Verification */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-green-600 mt-0.5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              <div className="text-left">
                <p className="text-sm font-medium text-green-800 mb-1">WhatsApp OTP Sent!</p>
                <p className="text-sm text-green-700">
                  Enter the 6-digit OTP sent to <strong>+91 {formData.phone}</strong>
                </p>
              </div>
            </div>
          </div>
          
          {/* Login ID Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
            <p className="text-sm text-blue-800">
              Your Login ID: <strong>{formData.email}</strong>
            </p>
          </div>
          
          <Button onClick={() => navigate('/verify-otp?phone=' + formData.phone)} className="w-full bg-green-600 hover:bg-green-700 mb-2">
            Enter WhatsApp OTP
          </Button>
          <Button onClick={() => navigate('/login')} variant="outline" className="w-full">
            Go to Login
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-start justify-center p-4 pt-8">
      <div className="max-w-6xl w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* Left side - Info */}
          <div className="flex flex-col lg:sticky lg:top-8">
            <div className="mb-6 text-center">
              <div className="flex justify-center mb-3">
                <img src="/himt-logo.png" alt="HIMT Logo" className="h-20 w-auto" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-1">MOCK IMU-CET Test</h1>
              <p className="text-lg text-gray-600 mb-4">Hindustan Institute of Maritime Training</p>
              
              {/* Admissions Announcement */}
              <a 
                href="https://www.himtcollege.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg p-4 shadow-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 cursor-pointer"
              >
                <p className="text-sm font-semibold uppercase tracking-wide mb-1">Admissions Open 2026-2027</p>
                <p className="text-base">
                  <span className="font-bold">BSc Nautical Science</span> | <span className="font-bold">B.Tech Marine Engineering</span>
                </p>
                <p className="text-xs mt-2 opacity-90 underline hover:opacity-100">Apply now at HIMT College →</p>
              </a>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-0.5">Comprehensive Test</h3>
                  <p className="text-sm text-gray-600">Practice with real IMU-CET exam pattern and difficulty level</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-0.5">Timed Assessment</h3>
                  <p className="text-sm text-gray-600">Experience real exam conditions with time limits</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-0.5">Instant Results</h3>
                  <p className="text-sm text-gray-600">Get detailed performance analysis after completion</p>
                </div>
              </div>
            </div>

            {/* IMU CET Important Dates */}
            <div className="mt-6 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-2">
                <h3 className="text-white font-semibold flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  IMU-CET 2026 Important Dates
                </h3>
              </div>
              <div className="p-4">
                <div className="space-y-3">
                  <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                    <span className="text-sm text-gray-600">Online Registration Start Date</span>
                    <span className="text-sm font-semibold text-gray-900">6th February 2026</span>
                  </div>
                  <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                    <span className="text-sm text-gray-600">Last Date for Online Registration</span>
                    <span className="text-sm font-semibold text-gray-900">24th April 2026</span>
                  </div>
                  <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                    <span className="text-sm text-gray-600">Online City Selection Start Date</span>
                    <span className="text-sm font-semibold text-gray-900">4th May 2026</span>
                  </div>
                  <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                    <span className="text-sm text-gray-600">Online City Selection End Date</span>
                    <span className="text-sm font-semibold text-gray-900">10th May 2026</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 font-medium">Date of IMU-CET</span>
                    <span className="text-sm font-bold text-orange-600">24th May 2026</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Registration Form */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Register for Mock Test</h2>
            <p className="text-gray-600 mb-6">Create your account to access the MOCK IMU-CET test</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && (
                  <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" /> {errors.name}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={errors.email ? 'border-red-500' : ''}
                />
                {errors.email && (
                  <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" /> {errors.email}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="phone">Mobile Number (WhatsApp) *</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="10 digit mobile number"
                  maxLength="10"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '') })}
                  className={errors.phone ? 'border-red-500' : ''}
                />
                <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                  <svg className="w-3 h-3 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  OTP will be sent to this number via WhatsApp
                </p>
                {errors.phone && (
                  <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" /> {errors.phone}
                  </p>
                )}
              </div>

              <div>
                <Label className="text-base font-semibold">Course Preferences *</Label>
                <p className="text-xs text-gray-500 mb-3">Select your course choices in order of preference</p>
                
                {/* 1st Choice */}
                <div className="mb-3">
                  <Label htmlFor="course_choice_1" className="text-sm">1st Choice *</Label>
                  <select
                    id="course_choice_1"
                    value={formData.course_choice_1}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      course_choice_1: e.target.value,
                      // Clear 2nd and 3rd choice if they conflict with new 1st choice
                      course_choice_2: formData.course_choice_2 === e.target.value ? '' : formData.course_choice_2,
                      course_choice_3: formData.course_choice_3 === e.target.value ? '' : formData.course_choice_3
                    })}
                    className={`flex h-10 w-full rounded-md border ${errors.course_choice_1 ? 'border-red-500' : 'border-input'} bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2`}
                  >
                    {COURSE_OPTIONS.map((course) => (
                      <option key={course.value} value={course.value}>
                        {course.label}
                      </option>
                    ))}
                  </select>
                  {errors.course_choice_1 && (
                    <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" /> {errors.course_choice_1}
                    </p>
                  )}
                </div>

                {/* 2nd Choice - Filter out 1st choice */}
                <div className="mb-3">
                  <Label htmlFor="course_choice_2" className="text-sm">2nd Choice (Optional)</Label>
                  <select
                    id="course_choice_2"
                    value={formData.course_choice_2}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      course_choice_2: e.target.value,
                      // Clear 3rd choice if it conflicts with new 2nd choice
                      course_choice_3: formData.course_choice_3 === e.target.value ? '' : formData.course_choice_3
                    })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    disabled={!formData.course_choice_1}
                  >
                    <option value="">Select a course</option>
                    {COURSE_OPTIONS.filter(course => 
                      course.value && course.value !== formData.course_choice_1
                    ).map((course) => (
                      <option key={course.value} value={course.value}>
                        {course.label}
                      </option>
                    ))}
                  </select>
                  {!formData.course_choice_1 && (
                    <p className="text-xs text-gray-500 mt-1">Select 1st choice first</p>
                  )}
                </div>

                {/* 3rd Choice - Filter out 1st and 2nd choice */}
                <div>
                  <Label htmlFor="course_choice_3" className="text-sm">3rd Choice (Optional)</Label>
                  <select
                    id="course_choice_3"
                    value={formData.course_choice_3}
                    onChange={(e) => setFormData({ ...formData, course_choice_3: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    disabled={!formData.course_choice_1 || !formData.course_choice_2}
                  >
                    <option value="">Select a course</option>
                    {COURSE_OPTIONS.filter(course => 
                      course.value && 
                      course.value !== formData.course_choice_1 && 
                      course.value !== formData.course_choice_2
                    ).map((course) => (
                      <option key={course.value} value={course.value}>
                        {course.label}
                      </option>
                    ))}
                  </select>
                  {(!formData.course_choice_1 || !formData.course_choice_2) && (
                    <p className="text-xs text-gray-500 mt-1">Select 1st and 2nd choice first</p>
                  )}
                </div>
              </div>

              {/* HIMT Application Status */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Have you applied for any course at HIMT College? *</Label>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="has_applied"
                      checked={formData.has_applied === true}
                      onChange={() => setFormData({ ...formData, has_applied: true })}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm text-gray-700">Yes</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="has_applied"
                      checked={formData.has_applied === false}
                      onChange={() => setFormData({ ...formData, has_applied: false, application_number: '' })}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm text-gray-700">No</span>
                  </label>
                </div>
                {errors.has_applied && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" /> {errors.has_applied}
                  </p>
                )}
                
                {/* Application Number - shown only when Yes is selected */}
                {formData.has_applied === true && (
                  <div className="mt-3">
                    <Label htmlFor="application_number">Application Number *</Label>
                    <Input
                      id="application_number"
                      type="text"
                      placeholder="Enter your HIMT application number"
                      value={formData.application_number}
                      onChange={(e) => setFormData({ ...formData, application_number: e.target.value })}
                      className={errors.application_number ? 'border-red-500' : ''}
                    />
                    {errors.application_number && (
                      <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" /> {errors.application_number}
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="password">Create Password *</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Min 8 characters, 1 number, 1 symbol"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className={errors.password ? 'border-red-500' : ''}
                />
                {errors.password && (
                  <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" /> {errors.password}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="confirmPassword">Confirm Password *</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Re-enter your password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className={errors.confirmPassword ? 'border-red-500' : ''}
                />
                {errors.confirmPassword && (
                  <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" /> {errors.confirmPassword}
                  </p>
                )}
              </div>

              {/* Consent Checkboxes */}
              <div className="space-y-4 pt-2">
                {/* Marketing Consent */}
                <div className="flex items-start space-x-3">
                  <Checkbox 
                    id="marketing-consent"
                    checked={acceptMarketing}
                    onCheckedChange={setAcceptMarketing}
                    className={`mt-1 ${!acceptMarketing ? 'border-amber-400' : ''}`}
                  />
                  <Label htmlFor="marketing-consent" className="text-sm text-gray-700 leading-relaxed cursor-pointer">
                    I'm happy to receive updates from HIMT College about courses, admissions, and events via Email, SMS, WhatsApp, or phone calls, as outlined in the Privacy Policy. *
                  </Label>
                </div>

                {/* Terms & Conditions */}
                <div className="flex items-start space-x-3">
                  <Checkbox 
                    id="terms-consent"
                    checked={acceptTerms}
                    onCheckedChange={setAcceptTerms}
                    className={`mt-1 ${errors.terms ? 'border-red-500' : ''}`}
                  />
                  <div className="flex-1">
                    <Label htmlFor="terms-consent" className="text-sm text-gray-700 leading-relaxed cursor-pointer">
                      I have read the{' '}
                      <a 
                        href="https://www.himtcollege.com/privacy-policy/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline inline-flex items-center gap-1 font-medium"
                      >
                        Terms & Conditions
                        <ExternalLink className="w-3 h-3" />
                      </a> *
                    </Label>
                  </div>
                </div>
                {errors.terms && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" /> {errors.terms}
                  </p>
                )}
              </div>

              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                disabled={loading || !acceptMarketing || !acceptTerms}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Registering...
                  </>
                ) : (
                  'Register Now'
                )}
              </Button>

              <p className="text-center text-sm text-blue-700 bg-blue-50 p-2 rounded-md">
                Your email address will be used as your Login ID
              </p>

              <p className="text-center text-sm text-gray-600">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-blue-600 hover:underline font-medium"
                >
                  Login here
                </button>
              </p>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default PublicRegistration;
